import apiConfig from './api.config';
import logger from '../logger';

class API {
  constructor(configObject) {
    this.config = configObject;
    this.retryAttempts = 3;
    this.retryDelay = 1000; // 1 second base delay
  }

  // Helper method to determine if an error is retryable
  isRetryableError(status, error) {
    // Retry on network errors, server errors (5xx), or rate limiting (429)
    return !status || status >= 500 || status === 429 || error?.name === 'NetworkError';
  }

  // Helper method to implement exponential backoff
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Enhanced fetch wrapper with retry logic and error handling
  async fetchWithRetry(url, options, route, method) {
    let lastError;
    
    for (let attempt = 1; attempt <= this.retryAttempts; attempt++) {
      try {
        logger.debug(`API ${method} request`, { 
          route, 
          attempt, 
          url: url.replace(/\/\/[^\/]+/, '//[DOMAIN]') // Hide domain in logs
        });

        const res = await fetch(url, options);
        const status = res.status;
        const headers = res.headers;

        // Handle different response types
        let response;
        const contentType = headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          response = await res.json();
        } else {
          response = await res.text();
        }

        // Log successful requests
        if (status >= 200 && status < 300) {
          logger.info(`API ${method} success`, { 
            route, 
            status, 
            responseSize: JSON.stringify(response).length 
          });
        } else if (status >= 400 && status < 500) {
          // Client errors - don't retry
          logger.warn(`API ${method} client error`, { 
            route, 
            status, 
            error: response?.message || response 
          });
        } else if (status >= 500) {
          // Server errors - log and potentially retry
          logger.error(`API ${method} server error`, { 
            route, 
            status, 
            attempt,
            error: response?.message || response 
          });
        }

        return { status, headers, response };

      } catch (error) {
        lastError = error;
        logger.error(`API ${method} network error`, { 
          route, 
          attempt, 
          error: error.message,
          stack: error.stack 
        });

        // Don't retry on the last attempt
        if (attempt === this.retryAttempts) {
          break;
        }

        // Only retry if it's a retryable error
        if (this.isRetryableError(null, error)) {
          const delayMs = this.retryDelay * Math.pow(2, attempt - 1); // Exponential backoff
          logger.info(`Retrying API ${method} request`, { 
            route, 
            attempt: attempt + 1, 
            delayMs 
          });
          await this.delay(delayMs);
        } else {
          break;
        }
      }
    }

    // If we get here, all retries failed
    logger.error(`API ${method} failed after ${this.retryAttempts} attempts`, { 
      route, 
      lastError: lastError?.message 
    });
    
    throw lastError || new Error(`Failed to complete ${method} request to ${route}`);
  }

  async get(token, route, stage = 'apiv5') {
    try {
      const options = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
        mode: 'cors',
        accept: '*/*'
      };

      const url = this.config.domain + `/${stage}/${route}`;
      return await this.fetchWithRetry(url, options, route, 'GET');
      
    } catch (error) {
      // Final error handling - return a structured error response
      logger.error('API GET request failed completely', { 
        route, 
        stage, 
        error: error.message 
      });
      
      return {
        status: 0,
        headers: null,
        response: {
          error: true,
          message: 'Network error - please check your connection and try again',
          originalError: error.message
        }
      };
    }
  }

  async post(token, route, payload, stage = 'apiv5') {
    try {
      const options = {
        method: 'POST',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          authorization: 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      };

      const url = this.config.domain + `/${stage}/${route}`;
      return await this.fetchWithRetry(url, options, route, 'POST');
      
    } catch (error) {
      logger.error('API POST request failed completely', { 
        route, 
        stage, 
        error: error.message 
      });
      
      return {
        status: 0,
        headers: null,
        response: {
          error: true,
          message: 'Network error - please check your connection and try again',
          originalError: error.message
        }
      };
    }
  }

  async put(token, route, payload, stage = 'apiv5') {
    try {
      const options = {
        method: 'PUT',
        headers: {
          'content-type': 'application/json',
          authorization: 'Bearer ' + token
        },
        body: JSON.stringify(payload)
      };

      const url = this.config.domain + `/${stage}/${route}`;
      return await this.fetchWithRetry(url, options, route, 'PUT');
      
    } catch (error) {
      logger.error('API PUT request failed completely', { 
        route, 
        stage, 
        error: error.message 
      });
      
      return {
        status: 0,
        headers: null,
        response: {
          error: true,
          message: 'Network error - please check your connection and try again',
          originalError: error.message
        }
      };
    }
  }

  async delete(token, route, stage = 'apiv5') {
    try {
      const options = {
        method: 'DELETE',
        mode: 'cors',
        accept: '*/*',
        credentials: 'include',
        headers: {
          'content-type': 'application/json',
          authorization: 'Bearer ' + token
        }
      };

      const url = this.config.domain + `/${stage}/${route}`;
      return await this.fetchWithRetry(url, options, route, 'DELETE');
      
    } catch (error) {
      logger.error('API DELETE request failed completely', { 
        route, 
        stage, 
        error: error.message 
      });
      
      return {
        status: 0,
        headers: null,
        response: {
          error: true,
          message: 'Network error - please check your connection and try again',
          originalError: error.message
        }
      };
    }
  }
}

const api = new API(apiConfig);
export default api;